import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@apollo/react-hooks'
import { Link } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table'
import { costChangeStatusVariant } from '@/lib/status-variants'
import { cn } from '@/lib/utils'
import * as Query from '../../../api/gql/query'
import { useRouter } from '../../../hooks'
import ChangeDialog from '../changes/ChangeDialog'
import { useChangeMutations } from '../hooks/useChangeMutations'
import { describeCostControlError } from '../lib/errors'
import { changeStatusView } from '../lib/changes'
import { CHANGE_KIND_KEY } from '../lib/labels'
import { formatMoney, isNegativeMoney } from '../lib/money'
import type { CostChange, CostContract } from '../types'
import ContractRecordsSkeleton from './ContractRecordsSkeleton'

interface Props { contract: CostContract; contracts: CostContract[]; projectId: string; currency: string; locale: string; canCreate: boolean }

/** The contract's changes (P9 deferred them to 3b): contract changes of a subcontract, budget changes naming the main contract. */
const ContractChangesCard: React.FC<Props> = ({ contract, contracts, projectId, currency, locale, canCreate }) => {
  const { t } = useTranslation()
  const { history } = useRouter()
  const mutations = useChangeMutations(projectId)
  const [creating, setCreating] = useState(false)
  const { data, loading, error } = useQuery<{ costChanges: CostChange[] }>(Query.COST_CHANGES, {
    variables: { projectId, filter: { contractId: contract.id } },
    fetchPolicy: 'cache-and-network',
    context: { handlesErrors: true },
  })
  const rows = data?.costChanges
  const canAdd = canCreate && (contract.kind === 'MAIN' || contract.status === 'ACTIVE')
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-base">{t('CostControl_contract_changes_title')}</CardTitle>
        {canAdd && (
          <Button variant="outline" size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            {t('CostControl_change_new')}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {!rows && loading && <ContractRecordsSkeleton />}
        {!rows && error && <p className="text-sm text-destructive">{describeCostControlError(error.message, t)}</p>}
        {rows && rows.length === 0 && <p className="text-sm text-muted-foreground">{t('CostControl_contract_changes_empty')}</p>}
        {rows && rows.length > 0 && (
          <Table>
            <TableBody>
              {rows.map((c) => {
                const status = changeStatusView(c)
                return (
                  <TableRow key={c.id}>
                    <TableCell className="w-12 font-mono text-xs">{c.number}</TableCell>
                    <TableCell>
                      <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/changes/${c.id}`}>{c.title}</Link>
                      {c.kind === 'POTENTIAL' && <Badge variant="neutral" className="ml-2">{t(CHANGE_KIND_KEY[c.kind])}</Badge>}
                      {c.takenOver && <Badge variant="secondary" className="ml-2">{t('CostControl_change_taken_over')}</Badge>}
                    </TableCell>
                    <TableCell className="w-40"><Badge variant={costChangeStatusVariant(status.tone)}>{t(status.key)}</Badge></TableCell>
                    <TableCell className={cn('w-36 text-right tabular-nums', isNegativeMoney(c.total) && 'text-destructive')}>{formatMoney(c.total, { locale, currency })}</TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
      <ChangeDialog
        open={creating}
        mode="create"
        contracts={contracts}
        initialKind={contract.kind === 'MAIN' ? 'BUDGET_CHANGE' : 'CONTRACT_CHANGE'}
        initialContractId={contract.id}
        onOpenChange={setCreating}
        onSubmit={async (input) => {
          const res = await mutations.create(input)
          if (res.ok) history.push(`/project/${projectId}/cost-control/changes/${res.data.createCostChange.id}`)
          return res.ok
        }}
      />
    </Card>
  )
}
export default ContractChangesCard
